import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Paperclip, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = ["Confirmé", "À vérifier", "Contesté", "Information manquante"];
const EMPTY = { date: "", heure: "", description_factuelle: "", source: "", fonction_source: "", type_source: "", statut_verification: "À vérifier", commentaire: "", url_preuve: "" };

export default function FactDialog({ open, onOpenChange, analysisId, initial, onSubmit, submitting }) {
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !initial?.ID_FAIT) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/hfacs/${analysisId}/facts/${initial.ID_FAIT}/upload`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, url_preuve: res.data.url }));
      toast.success("Preuve envoyée vers Google Drive.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi du fichier.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fact-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase">{initial ? "Modifier le fait" : "Ajouter un fait"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" data-testid="fact-form-date" value={form.date} onChange={update("date")} />
            </div>
            <div>
              <Label>Heure</Label>
              <Input type="time" data-testid="fact-form-heure" value={form.heure} onChange={update("heure")} />
            </div>
          </div>
          <div>
            <Label>Description factuelle</Label>
            <Textarea required rows={3} data-testid="fact-form-description" value={form.description_factuelle} onChange={update("description_factuelle")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Source / personne</Label>
              <Input data-testid="fact-form-source" value={form.source} onChange={update("source")} />
            </div>
            <div>
              <Label>Fonction de la source</Label>
              <Input data-testid="fact-form-fonction" value={form.fonction_source} onChange={update("fonction_source")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type de source</Label>
              <Input placeholder="Témoignage, document, mesure..." data-testid="fact-form-type-source" value={form.type_source} onChange={update("type_source")} />
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={form.statut_verification} onValueChange={(v) => setForm({ ...form, statut_verification: v })}>
                <SelectTrigger data-testid="fact-form-statut-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Preuve / document</Label>
            <div className="flex items-center gap-2">
              <Input data-testid="fact-form-preuve" placeholder="URL (ou joindre un fichier)" value={form.url_preuve} onChange={update("url_preuve")} />
              {initial?.ID_FAIT && (
                <>
                  <input ref={fileInputRef} type="file" className="hidden" onChange={handleFile} data-testid="fact-form-file-input" />
                  <Button type="button" variant="outline" size="icon" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid="fact-form-upload-button">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  </Button>
                </>
              )}
            </div>
            {form.url_preuve && (
              <a href={form.url_preuve} target="_blank" rel="noopener noreferrer" className="text-xs text-accent flex items-center gap-1 mt-1">
                <ExternalLink className="h-3 w-3" /> Ouvrir la preuve
              </a>
            )}
            {!initial?.ID_FAIT && (
              <p className="text-xs text-muted-foreground mt-1">Enregistrez d'abord le fait pour pouvoir joindre une photo ou un document.</p>
            )}
          </div>
          <div>
            <Label>Commentaire</Label>
            <Textarea rows={2} data-testid="fact-form-commentaire" value={form.commentaire} onChange={update("commentaire")} />
          </div>
          <Button type="submit" disabled={submitting} data-testid="fact-form-submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : initial ? "Enregistrer" : "Ajouter"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
